import axios from "axios";
import { Observable } from "rxjs";
import { NotImplementedError } from "../../../errors";
import { ObservableHandler } from "../../../handlers";
import { ProposalsFetchFailed } from "./errors";
import { DEFAULT_PAGE_SIZE, toQueryParams } from "./utils";
/**
 * Read-only handler for the seller service's `ListProposals` endpoint.
 *
 * Backed by `GET ${sdk.urls.sellerBaseUrl}/v1/proposal`. Returns `Proposal`
 * typed directly from `@madhive/mad-types/openapi/seller/sellerSchemas` — no
 * local model or transformer is defined, since mad-types already models the
 * wire shape.
 *
 * Observable rather than promise-based specifically so pagination works:
 * `useLoadListData` only inspects results for a `Page` on its observable
 * branch, so a promise handler can never populate `ListData.pagination`.
 * Mirrors the Deals and Advanced Exports Templates handlers.
 *
 * CRUD (save / delete / create / make) is intentionally NOT implemented — this
 * module is list-only per MVSLLR-1345. Later tickets can add write ops as needed.
 */
class Proposals extends ObservableHandler {
    constructor(sdk) {
        super(sdk, "proposals", { atomize: true });
    }
    findItems(filters, sort) {
        return new Observable((subscriber) => {
            this.getProposals(filters, sort)
                .then((proposals) => {
                subscriber.next(proposals);
                subscriber.complete();
            })
                .catch((error) => {
                subscriber.error(error);
            });
        });
    }
    getProposals(filters, sort) {
        const url = `${this.collectionUrl}${toQueryParams(filters, sort)}`;
        const pageSize = filters?.paging?.size || DEFAULT_PAGE_SIZE;
        return this.cache
            .promise(url, () => axios
            .get(url, {
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data }) => {
            const proposals = data.proposals ?? [];
            // Only page when the caller asked to. Without `paging` the caller
            // wants a plain list (e.g. CSV export), and wrapping it in a Page
            // would put a page object where an array is expected.
            if (!filters?.paging) {
                return proposals;
            }
            return {
                page: {
                    // total_size is an int64, so it arrives as a string.
                    count: Number(data.total_size ?? 0),
                    token: data.next_page_token,
                    size: pageSize
                },
                data: proposals
            };
        }))
            .catch((error) => {
            throw new ProposalsFetchFailed(error);
        });
    }
    get collectionUrl() {
        // Path matches the mad-types-generated fetcher exactly — see
        // `mad-web/packages/core/mad-types/src/openapi/seller/sellerComponents.ts`
        // (`fetchProposalServiceListProposals` → `url: "/v1/proposal"`).
        return `${this.sdk.urls.sellerBaseUrl}/v1/proposal`;
    }
    saveItem() {
        throw new NotImplementedError("save");
    }
    deleteItem() {
        throw new NotImplementedError("delete");
    }
    /**
     * @deprecated
     * `make` is not supported — Proposals is list-only.
     */
    make() {
        throw new NotImplementedError("make");
    }
}
export default Proposals;
//# sourceMappingURL=index.js.map